import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslate } from 'react-admin';

import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { Resources } from '../resource';
import { SplashScreen } from 'src/components/loading-screen';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import ErrorPage403 from 'src/pages/error/403';

import {
  getRewardCampaignConfig,
  RewardCampaignConfigForm,
  RewardCampaignRuleForm,
  updateRewardCampaignConfig,
} from '../../services/rewardRulesConfigService';

type ToastState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
};

function LoadingBlock() {
  return (
    <Box
      sx={{
        minHeight: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <SplashScreen />
    </Box>
  );
}

function toInputString(value: unknown) {
  if (value === null || value === undefined) return '';
  return String(value);
}

function buildEmptyRule(sortOrder: number): RewardCampaignRuleForm {
  return {
    thresholdUsdc: '',
    rewardUsdc: '',
    sortOrder,
    isActive: true,
  };
}

export default function List() {
  const translate = useTranslate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<RewardCampaignConfigForm | null>(null);
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showToast = useCallback(
    (
      message: string,
      severity: ToastState['severity'] = 'success',
    ) => {
      setToast({
        open: true,
        message,
        severity,
      });
    },
    [],
  );

  const handleCloseToast = useCallback(
    (_?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') return;
      setToast((prev) => ({ ...prev, open: false }));
    },
    [],
  );

  const normalizeResponseToForm = useCallback(
    (data: RewardCampaignConfigForm): RewardCampaignConfigForm => {
      return {
        ...data,
        id: toInputString(data.id),
        campaignId: data.campaignId ? toInputString(data.campaignId) : undefined,
        campaignKey: toInputString(data.campaignKey),
        campaignType: data.campaignType
          ? toInputString(data.campaignType)
          : undefined,
        displayName: toInputString(data.displayName),
        currency: toInputString(data.currency || 'USDC'),
        depositThresholdUsdc: toInputString(data.depositThresholdUsdc),
        totalRewardCapUsdc: toInputString(data.totalRewardCapUsdc),
        isActive: !!data.isActive,
        rules: [...(data.rules || [])]
          .map((r) => ({
            ...r,
            id: r.id ? toInputString(r.id) : undefined,
            ruleKey: r.ruleKey ? toInputString(r.ruleKey) : undefined,
            ruleName: r.ruleName ? toInputString(r.ruleName) : undefined,
            thresholdUsdc: toInputString(r.thresholdUsdc),
            rewardUsdc: toInputString(r.rewardUsdc),
            sortOrder: Number(r.sortOrder || 0),
            isActive: !!r.isActive,
          }))
          .sort((a, b) => a.sortOrder - b.sortOrder),
      };
    },
    [],
  );

  const loadDetail = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getRewardCampaignConfig();
      setForm(normalizeResponseToForm(data));
    } catch (e: any) {
      showToast(
        e?.message ||
        translate('module.agentRewardRules.toast.loadFailed', {
          _: 'Failed to load',
        }),
        'error',
      );
    } finally {
      setLoading(false);
    }
  }, [normalizeResponseToForm, showToast, translate]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const setBaseField = <K extends keyof RewardCampaignConfigForm>(
    key: K,
    value: RewardCampaignConfigForm[K],
  ) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateRule = (
    index: number,
    key: keyof RewardCampaignRuleForm,
    value: RewardCampaignRuleForm[keyof RewardCampaignRuleForm],
  ) => {
    setForm((prev) => {
      if (!prev) return prev;
      const nextRules = [...prev.rules];
      nextRules[index] = {
        ...nextRules[index],
        [key]: value,
      };
      return { ...prev, rules: nextRules };
    });
  };

  const handleAddRule = () => {
    setForm((prev) => {
      if (!prev) return prev;
      const nextSort =
        prev.rules.length > 0
          ? Math.max(...prev.rules.map((r) => Number(r.sortOrder) || 0)) + 1
          : 1;

      return {
        ...prev,
        rules: [...prev.rules, buildEmptyRule(nextSort)],
      };
    });
  };

  const handleDeleteRule = (index: number) => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rules: prev.rules.filter((_, i) => i !== index),
      };
    });
  };

  const validateForm = () => {
    if (!form) {
      return translate('module.agentRewardRules.validation.emptyForm', {
        _: 'Form is empty',
      });
    }

    if (!form.depositThresholdUsdc) {
      return translate(
        'module.agentRewardRules.validation.depositThresholdRequired',
        { _: 'Please enter the cumulative deposit threshold' },
      );
    }

    if (!form.totalRewardCapUsdc) {
      return translate(
        'module.agentRewardRules.validation.totalRewardCapRequired',
        { _: 'Please enter the total reward cap' },
      );
    }

    if (!form.currency) {
      return translate('module.agentRewardRules.validation.currencyRequired', {
        _: 'Please enter the currency',
      });
    }

    if (!form.rules.length) {
      return translate('module.agentRewardRules.validation.atLeastOneRule', {
        _: 'Please keep at least one rule',
      });
    }

    const depositThreshold = Number(form.depositThresholdUsdc);
    const totalRewardCap = Number(form.totalRewardCapUsdc);
    const thresholds = form.rules.map((r) => Number(r.thresholdUsdc));
    const rewards = form.rules.map((r) => Number(r.rewardUsdc));
    const sortOrders = form.rules.map((r) => Number(r.sortOrder));

    if (!Number.isFinite(depositThreshold) || depositThreshold < 0) {
      return translate(
        'module.agentRewardRules.validation.depositThresholdInvalid',
        { _: 'The cumulative deposit threshold must be greater than or equal to 0' },
      );
    }

    if (!Number.isFinite(totalRewardCap) || totalRewardCap <= 0) {
      return translate(
        'module.agentRewardRules.validation.totalRewardCapInvalid',
        { _: 'The total reward cap must be greater than 0' },
      );
    }

    if (thresholds.some((n) => !Number.isFinite(n) || n <= 0)) {
      return translate('module.agentRewardRules.validation.thresholdInvalid', {
        _: 'The rule threshold must be greater than 0',
      });
    }

    if (rewards.some((n) => !Number.isFinite(n) || n <= 0)) {
      return translate('module.agentRewardRules.validation.rewardInvalid', {
        _: 'The reward amount must be greater than 0',
      });
    }

    if (sortOrders.some((n) => !Number.isFinite(n) || n <= 0)) {
      return translate('module.agentRewardRules.validation.sortOrderInvalid', {
        _: 'The sort order must be greater than 0',
      });
    }

    if (new Set(thresholds).size !== thresholds.length) {
      return translate(
        'module.agentRewardRules.validation.thresholdDuplicated',
        { _: 'Rule thresholds cannot be duplicated' },
      );
    }

    if (new Set(sortOrders).size !== sortOrders.length) {
      return translate(
        'module.agentRewardRules.validation.sortOrderDuplicated',
        { _: 'Sort orders cannot be duplicated' },
      );
    }

    const sortedThresholds = [...thresholds].sort((a, b) => a - b);
    if (JSON.stringify(sortedThresholds) !== JSON.stringify(thresholds)) {
      return translate('module.agentRewardRules.validation.thresholdNotAsc', {
        _: 'Rule thresholds must be in ascending order',
      });
    }

    const activeRewardTotal = form.rules
      .filter((r) => r.isActive)
      .reduce((sum, r) => sum + Number(r.rewardUsdc || 0), 0);

    if (activeRewardTotal > totalRewardCap) {
      return translate('module.agentRewardRules.validation.rewardExceeded', {
        _: 'The sum of enabled rewards cannot exceed the total reward cap',
      });
    }

    return '';
  };

  const handleSave = async () => {
    const msg = validateForm();
    if (msg) {
      showToast(msg, 'warning');
      return;
    }

    if (!form) return;

    setSaving(true);

    try {
      const data = await updateRewardCampaignConfig(form);
      setForm(normalizeResponseToForm(data));
      showToast(
        translate('module.agentRewardRules.toast.saveSuccess', {
          _: 'Saved successfully',
        }),
        'success',
      );
    } catch (e: any) {
      showToast(
        e?.message ||
        translate('module.agentRewardRules.toast.saveFailed', {
          _: 'Failed to save',
        }),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  const activeRewardTotal = useMemo(() => {
    if (!form) return 0;
    return form.rules
      .filter((r) => r.isActive)
      .reduce((sum, r) => sum + Number(r.rewardUsdc || 0), 0);
  }, [form]);

  if (loading) {
    return (
      <DashboardContent>
        <PermissionGuard
          resource="agent-reward-rules"
          action="read"
          fallback={<ErrorPage403 />}
        >
          <CustomBreadcrumbs
            heading={translate('module.agentRewardRules.title', {
              _: 'Reward Rules',
            })}
            links={[
              {
                name: translate('module.agentRewardRules.title', {
                  _: 'Reward Rules',
                }),
                href: `/${Resources.agentRewardRules}`,
              },
            ]}
          />
          <Card sx={{ mt: 3 }}>
            <LoadingBlock />
          </Card>
        </PermissionGuard>

        <Snackbar
          open={toast.open}
          autoHideDuration={3000}
          onClose={handleCloseToast}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseToast}
            severity={toast.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </DashboardContent>
    );
  }

  return (
    <DashboardContent>
      <PermissionGuard
        resource="agent-reward-rules"
        action="read"
        fallback={<ErrorPage403 />}
      >
        <CustomBreadcrumbs
          heading={translate('module.agentRewardRules.title', {
            _: 'Reward Rules',
          })}
          links={[
            {
              name: translate('module.agentRewardRules.title', {
                _: 'Reward Rules',
              }),
              href: `/${Resources.agentRewardRules}`,
            },
            {
              name: translate('module.agentRewardRules.list', { _: 'List' }),
            },
          ]}
        />

        {!!form && (
          <>
            <Card sx={{ mt: 3 }}>
              <Box sx={{ p: 3 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography variant="h6">
                    {translate('module.agentRewardRules.basicConfig', {
                      _: 'Basic Configuration',
                    })}
                  </Typography>

                  {/* <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving
                      ? translate('module.agentRewardRules.saving', {
                        _: 'Saving...',
                      })
                      : translate('module.agentRewardRules.saveConfig', {
                        _: 'Save Configuration',
                      })}
                  </Button> */}
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={translate('module.agentRewardRules.campaignName', {
                        _: 'Campaign Name',
                      })}
                      value={form.displayName}
                      onChange={(e) =>
                        setBaseField('displayName', e.target.value)
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={translate('module.agentRewardRules.currency', {
                        _: 'Currency',
                      })}
                      value={form.currency}
                      onChange={(e) => setBaseField('currency', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={translate(
                        'module.agentRewardRules.depositThresholdUsdc',
                        { _: 'Cumulative Deposit Threshold (USDC)' },
                      )}
                      value={form.depositThresholdUsdc}
                      onChange={(e) =>
                        setBaseField('depositThresholdUsdc', e.target.value)
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={translate(
                        'module.agentRewardRules.totalRewardCapUsdc',
                        { _: 'Total Reward Cap (USDC)' },
                      )}
                      value={form.totalRewardCapUsdc}
                      onChange={(e) =>
                        setBaseField('totalRewardCapUsdc', e.target.value)
                      }
                    />
                  </Grid>

                  {/* <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!form.isActive}
                          onChange={(e) =>
                            setBaseField('isActive', e.target.checked)
                          }
                        />
                      }
                      label={translate(
                        'module.agentRewardRules.campaignEnabled',
                        { _: 'Campaign Enabled' },
                      )}
                    />
                  </Grid> */}

                  {/* <Grid item xs={12} md={6}>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        {translate('module.agentRewardRules.campaignKey', {
                          _: 'Campaign Key',
                        })}
                      </Typography>
                      <Typography variant="body2">
                        {form.campaignKey || '-'}
                      </Typography>
                    </Stack>
                  </Grid> */}
                </Grid>
              </Box>
            </Card>

            <Card sx={{ mt: 3, mb: 3 }}>
              <Box sx={{ p: 3 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography variant="h6">
                    {translate('module.agentRewardRules.tradingRewardRules', {
                      _: 'Trading Volume Reward Rules',
                    })}
                  </Typography>

                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddRule}
                  >
                    {translate('module.agentRewardRules.addRule', {
                      _: 'Add Rule',
                    })}
                  </Button>
                </Stack>

                <Stack spacing={2}>
                  {form.rules.map((rule, index) => (
                    <Box
                      key={rule.id || `new-${index}`}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            type="number"
                            label={translate(
                              'module.agentRewardRules.thresholdUsdc',
                              { _: 'Trading Volume Threshold (USDC)' },
                            )}
                            value={rule.thresholdUsdc}
                            onChange={(e) =>
                              updateRule(index, 'thresholdUsdc', e.target.value)
                            }
                          />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            type="number"
                            label={translate(
                              'module.agentRewardRules.rewardUsdc',
                              { _: 'Reward Amount (USDC)' },
                            )}
                            value={rule.rewardUsdc}
                            onChange={(e) =>
                              updateRule(index, 'rewardUsdc', e.target.value)
                            }
                          />
                        </Grid>

                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            type="number"
                            label={translate(
                              'module.agentRewardRules.sortOrder',
                              { _: 'Sort Order' },
                            )}
                            value={rule.sortOrder}
                            onChange={(e) =>
                              updateRule(
                                index,
                                'sortOrder',
                                Number(e.target.value),
                              )
                            }
                          />
                        </Grid>

                        {/* <Grid item xs={12} md={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!rule.isActive}
                                onChange={(e) =>
                                  updateRule(index, 'isActive', e.target.checked)
                                }
                              />
                            }
                            label={translate('module.agentRewardRules.enabled', {
                              _: 'Enabled',
                            })}
                          />
                        </Grid> */}
                        <Grid
                          item
                          xs={12}
                          md
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteRule(index)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Stack>

                <Divider sx={{ my: 3 }} />

                {/* <Stack direction="row" spacing={4} flexWrap="wrap">
                  <Typography>
                    {translate(
                      'module.agentRewardRules.activeRewardTotal',
                      { _: 'Total Active Reward' },
                    )}
                    ：<b>{activeRewardTotal}</b>
                  </Typography>

                  <Typography>
                    {translate(
                      'module.agentRewardRules.totalRewardCapUsdc',
                      { _: 'Total Reward Cap (USDC)' },
                    )}
                    ：<b>{form.totalRewardCapUsdc}</b>
                  </Typography>

                  <Typography>
                    {translate(
                      'module.agentRewardRules.remainingQuota',
                      { _: 'Remaining Quota' },
                    )}
                    ：
                    <b>
                      {Number(form.totalRewardCapUsdc || 0) - activeRewardTotal}
                    </b>
                  </Typography>
                </Stack> */}

                <Box sx={{ mt: 3, textAlign: 'right' }}>

                <PermissionButton resource="agent-reward-rules" action="update">
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving
                      ? translate('module.agentRewardRules.saving', {
                        _: 'Saving...',
                      })
                      : translate('module.agentRewardRules.saveConfig', {
                        _: 'Save Configuration',
                      })}
                  </Button>
                  </PermissionButton>
                </Box>
              </Box>
            </Card>
          </>
        )}
      </PermissionGuard>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </DashboardContent>
  );
}